import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from "@stencil/core";
import { debounce } from "../../utils/debounce";

export type SelectEvent = number;

/**
 * @slot (default) - Thumbnail items
 */
@Component({
    tag: "uic-thumbnail",
    styleUrl: "thumbnail.scss",
    shadow: true
})
export class Thumbnail implements ComponentInterface {
    private scroller: HTMLDivElement;
    private observer: ResizeObserver;

    @State() showButtons = false;
    @State() scrolledToTop = false;
    @State() scrolledToBottom = false;

    @Element() host: HTMLUicThumbnailElement;

    /**
     * The number of pixels to scroll up or down when using the thumbnail buttons.
     * Defaults to the CSS custom property --uic-thumbnail-item-height.
     */
    @Prop() readonly scrollStep?: number;

    /**
     * The index of the selected thumbnail-item.
     */
    @Prop({ mutable: true }) selected = 0;

    /**
     * Emitted when the selection changes
     */
    @Event() uicSelect: EventEmitter<SelectEvent>;

    protected readonly debouncedCheckScrollPosition = debounce(() => this.checkScrollPosition(), 200);

    @Watch("selected")
    handleSelectedIndex(index: number) {
        const items = Array.from(this.getAllThumbnailItems());
        const item = items[index];
        this.selectThumbnailItem(item, false);
    }

    connectedCallback() {
        this.observer = new ResizeObserver(this.debouncedCheckScrollPosition);
    }

    componentDidLoad() {
        this.observer.observe(this.scroller);
        this.checkScrollPosition();

        const items = this.getAllThumbnailItems();
        const candidate = items[this.selected];
        this.selectThumbnailItem(candidate, false);
    }

    disconnectedCallback() {
        this.observer.disconnect();
    }

    private handleScroll() {
        this.checkScrollPosition();
    }

    private checkScrollPosition() {
        const el = this.scroller;

        this.scrolledToTop = el.scrollTop === 0;
        this.scrolledToBottom = el.scrollTop === el.scrollHeight - el.offsetHeight;
        this.showButtons = !(this.scrolledToTop && this.scrolledToBottom);
    }

    private scrollUp() {
        this.scroller.scrollBy({ top: -this.compiledScrollStep() });
    }

    private scrollDown() {
        this.scroller.scrollBy({ top: this.compiledScrollStep() });
    }

    private selectThumbnailItem(target: HTMLElement, emit = true) {
        // eslint-disable-next-line
        const selectedItem = target && target.closest("uic-thumbnail-item");
        // eslint-disable-next-line
        if (!selectedItem) {
            return;
        }

        const items = this.getAllThumbnailItems();
        const selectedIndex = items.indexOf(selectedItem);

        items.forEach((item) => (item.selected = item === selectedItem));

        this.selected = selectedIndex;

        if (emit) {
            this.uicSelect.emit(selectedIndex);
        }
    }

    private getAllThumbnailItems() {
        return Array.from(this.host.querySelectorAll("uic-thumbnail-item"));
    }

    private moveFocus(direction: number) {
        const items = this.getAllThumbnailItems();
        const activeIndex = items.findIndex((i) => i === document.activeElement);

        let nextIndex = activeIndex + direction;
        nextIndex = (nextIndex + items.length) % items.length;

        const nextItem = items[nextIndex];
        nextItem.focus();
    }

    private handleKeydown(e: KeyboardEvent) {
        const key = e.key;
        if (["ArrowLeft", "ArrowUp"].includes(key)) {
            this.moveFocus(-1);
            e.preventDefault();
        } else if (["ArrowRight", "ArrowDown"].includes(key)) {
            this.moveFocus(1);
            e.preventDefault();
        } else if (key === " ") {
            this.selectThumbnailItem(e.target as HTMLElement);
            e.preventDefault();
        }
    }

    private compiledScrollStep() {
        // eslint-disable-next-line
        if (this.scrollStep) {
            return this.scrollStep;
        }
        return this.getComputedThumbnailItemHeight();
    }

    private getComputedThumbnailItemHeight() {
        const propertyValue = getComputedStyle(this.host).getPropertyValue("--uic-thumbnail-item-height");
        return parseInt(propertyValue, 10) + 16 || 64;
    }

    render() {
        return (
            <Host
                class={{
                    "uic-thumbnail": true,
                    "uic-thumbnail--controls-visible": this.showButtons
                }}
            >
                <uic-icon-button
                    tabindex='-1'
                    class='uic-thumbnail__control'
                    icon='uic:chevron-up'
                    disabled={this.scrolledToTop}
                    onClick={() => this.scrollUp()}
                ></uic-icon-button>
                <div
                    class='uic-thumbnail__items'
                    ref={(el) => (this.scroller = el)}
                    onClick={(e) => this.selectThumbnailItem(e.target as HTMLElement)}
                    onScroll={() => this.handleScroll()}
                    onKeyDown={(e) => this.handleKeydown(e)}
                >
                    <slot></slot>
                </div>
                <uic-icon-button
                    tabindex='-1'
                    class='uic-thumbnail__control'
                    icon='uic:chevron-down'
                    disabled={this.scrolledToBottom}
                    onClick={() => this.scrollDown()}
                ></uic-icon-button>
            </Host>
        );
    }
}
